/**
 * E193.F1 (GUS-259, ADR-0139): o que falta a cada caso do envio, relido depois da conclusão.
 *
 * A conferência do kit roda em segundo plano depois da resposta da conclusão, e o recibo diz, por
 * caso, se ela está em andamento (E193.F1.C10, F-E193.F1-06). Enquanto algum caso estiver em
 * andamento, o Acompanhar relê a conclusão gravada a cada `INTERVALO_DAS_RELEITURAS_MS`: a lista
 * aparece quando a conferência termina, leve ela 4 ou 40 segundos. A releitura que falha só mantém
 * o que já estava na tela; depois de `LIMITE_DE_RELEITURAS` sem desfecho, a linha diz que a
 * conferência não terminou.
 */
import { useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import type { ArquivoPendenteData } from '@/lib/arquivos_pendentes';
import {
  lerConclusao,
  type CasoDoEnvioData,
  type ConclusaoDaAberturaData,
  type EstadoDaConferenciaDoKit,
} from '@/lib/conclusao_da_abertura';

export const INTERVALO_DAS_RELEITURAS_MS = 5_000;
/** Dez minutos de releituras: o prazo da marca da conferência no servidor. */
export const LIMITE_DE_RELEITURAS = 120;

export interface KitDoCaso {
  pendentes: ArquivoPendenteData[];
  conferencia: EstadoDaConferenciaDoKit | 'sem_desfecho';
}

export type KitPorCaso = Record<string, KitDoCaso>;

export function kitDoCaso(caso: CasoDoEnvioData): KitDoCaso {
  return {
    pendentes: caso.arquivos_pendentes ?? [],
    conferencia: caso.conferencia_do_kit ?? 'concluida',
  };
}

function kitDosCasos(conclusao: ConclusaoDaAberturaData): KitPorCaso {
  return Object.fromEntries(conclusao.casos.map((c) => [c.case_id, kitDoCaso(c)]));
}

function emConferencia(kits: KitPorCaso): boolean {
  return Object.values(kits).some(
    (k) => k.conferencia === 'em_andamento' || k.conferencia === 'falhou',
  );
}

function semDesfecho(kits: KitPorCaso): KitPorCaso {
  return Object.fromEntries(
    Object.entries(kits).map(([id, k]) => [
      id,
      k.conferencia === 'concluida' ? k : { ...k, conferencia: 'sem_desfecho' as const },
    ]),
  );
}

export function usePendentesDoEnvio(
  client: AxiosInstance,
  caseId: string,
  conclusao: ConclusaoDaAberturaData | null,
): KitPorCaso | null {
  const [kits, setKits] = useState<KitPorCaso | null>(null);
  useEffect(() => {
    if (conclusao === null) return;
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let atual = kitDosCasos(conclusao);
    let leituras = 0;
    const agendar = (): void => {
      if (!emConferencia(atual)) return;
      if (leituras >= LIMITE_DE_RELEITURAS) {
        setKits(semDesfecho(atual));
        return;
      }
      timer = setTimeout(reler, INTERVALO_DAS_RELEITURAS_MS);
    };
    const reler = (): void => {
      leituras += 1;
      lerConclusao(client, caseId).then(
        (lida) => {
          if (!vivo) return;
          if (lida !== null) {
            atual = kitDosCasos(lida);
            setKits(atual);
          }
          agendar();
        },
        () => vivo && agendar(),
      );
    };
    agendar();
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [client, caseId, conclusao]);
  return kits;
}
